import { useEffect, useMemo, useState } from 'react'
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, Legend,
} from 'recharts'
import { Download, ArrowUp, ArrowDown } from 'lucide-react'
import { Card, KpiCard, SectionTitle, EmptyState } from './AnalyticsUIKit'
import { getSalesInRange, getAllDailySummaries, fetchPapWarmers, getPapWarmers } from '../../lib/store'
import { getProducts, getPapConfig } from '../../data/products'
import { computePapAnalytics, papDaysToCsv } from '../../lib/papAnalytics'
import { formatR } from '../../lib/calculations'

function fmtShort(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  return d.toLocaleDateString('en-ZA', { day: '2-digit', month: 'short' })
}

function downloadCsv(filename, csv) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
}

// Enumerate every date between two ISO strings (inclusive).
function enumerateDates(from, to) {
  const out = []
  const a = new Date(from + 'T00:00:00')
  const b = new Date(to + 'T00:00:00')
  if (a > b) return out
  for (let d = new Date(a); d <= b; d.setDate(d.getDate() + 1)) {
    out.push(d.toISOString().slice(0, 10))
  }
  return out
}

export default function PapAnalyticsSection({ department, dateFrom, dateTo }) {
  const [papConfig, setPapConfig] = useState({ full: 20, half: 12, plate: 10 })
  const [warmerMap, setWarmerMap] = useState({})
  const [loadingWarmers, setLoadingWarmers] = useState(true)
  const [sortKey, setSortKey] = useState('date')
  const [sortDir, setSortDir] = useState('desc')

  useEffect(() => {
    getPapConfig().then(setPapConfig).catch(() => {})
  }, [])

  // Load papWarmers for every date in range. Fetch is async (Firestore REST)
  // with a local-cache fast path via getPapWarmers.
  useEffect(() => {
    let cancelled = false
    const dates = enumerateDates(dateFrom, dateTo)
    setLoadingWarmers(true)

    // Fast path: sync read from localStorage (populated by backgroundSyncForDate).
    const syncMap = {}
    for (const d of dates) {
      const v = getPapWarmers(department, d)
      if (v != null) syncMap[d] = v
    }
    setWarmerMap(syncMap)

    // Slow path: fetch any missing dates from Firestore. Dates the staff
    // never recorded legitimately remain undefined.
    ;(async () => {
      const missing = dates.filter(d => syncMap[d] == null)
      if (missing.length === 0) { setLoadingWarmers(false); return }
      const next = { ...syncMap }
      await Promise.all(missing.map(async d => {
        try {
          const v = await fetchPapWarmers(department, d)
          if (v != null) next[d] = v
        } catch { /* leave null */ }
      }))
      if (!cancelled) {
        setWarmerMap(next)
        setLoadingWarmers(false)
      }
    })()

    return () => { cancelled = true }
  }, [department, dateFrom, dateTo])

  const analytics = useMemo(() => {
    const dates = enumerateDates(dateFrom, dateTo)
    const allSales = getSalesInRange(department, dateFrom, dateTo)
    const salesByDate = {}
    for (const s of allSales) {
      const d = s.date
      if (!salesByDate[d]) salesByDate[d] = []
      salesByDate[d].push(s)
    }
    const products = getProducts(department)
    const dayInputs = dates.map(date => ({
      date,
      sales: salesByDate[date] || [],
      warmers: warmerMap[date] ?? null,
    }))
    // Skip days with no sales AND no warmer record — reduces noise.
    const nonEmpty = dayInputs.filter(d => d.sales.length > 0 || d.warmers != null)
    return computePapAnalytics(nonEmpty, products, papConfig)
  }, [department, dateFrom, dateTo, warmerMap, papConfig])

  const { days, summary } = analytics
  const hasData = days.length > 0

  const sortedDays = useMemo(() => {
    const copy = [...days]
    copy.sort((a, b) => {
      const av = a[sortKey]
      const bv = b[sortKey]
      // Nulls last
      if (av == null && bv == null) return 0
      if (av == null) return 1
      if (bv == null) return -1
      if (typeof av === 'number' && typeof bv === 'number') {
        return sortDir === 'asc' ? av - bv : bv - av
      }
      const s = String(av).localeCompare(String(bv))
      return sortDir === 'asc' ? s : -s
    })
    return copy
  }, [days, sortKey, sortDir])

  const toggleSort = (key) => {
    if (sortKey === key) setSortDir(d => (d === 'asc' ? 'desc' : 'asc'))
    else { setSortKey(key); setSortDir(key === 'date' ? 'desc' : 'desc') }
  }

  const headerBtn = (key, label, alignRight = false) => (
    <th
      className={`py-2 px-2 text-xs font-medium text-gray-400 cursor-pointer select-none hover:text-white ${alignRight ? 'text-right' : 'text-left'}`}
      onClick={() => toggleSort(key)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {sortKey === key && (sortDir === 'asc' ? <ArrowUp size={10} /> : <ArrowDown size={10} />)}
      </span>
    </th>
  )

  if (!hasData) {
    return (
      <>
        <SectionTitle>PAP Analytics</SectionTitle>
        <EmptyState message="No PAP sales or warmer records in this date range yet." />
      </>
    )
  }

  // Chart data — oldest-first timeline.
  const chartData = [...days].sort((a, b) => String(a.date).localeCompare(String(b.date))).map(d => ({
    date: fmtShort(d.date),
    revPerWarmer: d.revenuePerWarmer ?? 0,
    expected: d.expected ?? null,
    actual: d.totalRevenue,
  }))

  return (
    <>
      <SectionTitle>PAP Analytics</SectionTitle>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <KpiCard label="Avg R / Warmer" value={summary.avgRevenuePerWarmer > 0 ? formatR(summary.avgRevenuePerWarmer) : '—'} sub={`${summary.daysRecorded} days recorded`} />
        <KpiCard label="Total PAP Revenue" value={formatR(summary.totalRevenue)} sub="this period" />
        <KpiCard label="Days with Shortfall" value={String(summary.daysWithShortfall)} sub={`vs rolling avg`} color={summary.daysWithShortfall > 0 ? 'text-amber-400' : 'text-green-400'} />
        <KpiCard
          label="Biggest Shortfall"
          value={summary.biggestShortfall ? formatR(Math.abs(summary.biggestShortfall.amount)) : '—'}
          sub={summary.biggestShortfall ? `on ${fmtShort(summary.biggestShortfall.date)}` : 'no shortfalls'}
          color={summary.biggestShortfall ? 'text-red-400' : 'text-green-400'}
        />
      </div>

      {/* R/Warmer trend */}
      <Card className="mb-4">
        <h3 className="text-sm font-medium text-white mb-2">R / Warmer Trend</h3>
        <ResponsiveContainer width="100%" height={240}>
          <LineChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9CA3AF" fontSize={11} />
            <YAxis stroke="#9CA3AF" fontSize={11} tickFormatter={v => `R${v}`} />
            <Tooltip
              contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 12 }}
              formatter={(v) => formatR(v)}
            />
            <Line type="monotone" dataKey="revPerWarmer" stroke="#fbbf24" strokeWidth={2} dot={{ r: 2 }} name="R / Warmer" />
          </LineChart>
        </ResponsiveContainer>
      </Card>

      {/* Expected vs Actual */}
      <Card className="mb-4">
        <h3 className="text-sm font-medium text-white mb-2">Expected vs Actual Revenue</h3>
        <ResponsiveContainer width="100%" height={240}>
          <BarChart data={chartData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
            <XAxis dataKey="date" stroke="#9CA3AF" fontSize={11} />
            <YAxis stroke="#9CA3AF" fontSize={11} tickFormatter={v => `R${v}`} />
            <Tooltip
              contentStyle={{ background: '#111827', border: '1px solid #374151', fontSize: 12 }}
              formatter={(v) => v == null ? '—' : formatR(v)}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />
            <Bar dataKey="expected" fill="#64748b" name="Expected" />
            <Bar dataKey="actual" fill="#22c55e" name="Actual" />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* Data table */}
      <Card className="overflow-x-auto">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-medium text-white">Daily Detail</h3>
          <button
            onClick={() => downloadCsv(`pap-analytics-${department}-${dateFrom}-to-${dateTo}.csv`, papDaysToCsv(days))}
            className="flex items-center gap-1.5 text-xs bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-200 px-3 py-1.5 rounded"
          >
            <Download size={12} /> Export CSV
          </button>
        </div>
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-800">
              {headerBtn('date', 'Date')}
              {headerBtn('warmers', 'Warmers', true)}
              {headerBtn('papOnly', 'PAP Only', true)}
              {headerBtn('fullPap', 'Full', true)}
              {headerBtn('halfPap', 'Half', true)}
              {headerBtn('platePap', 'Plate', true)}
              {headerBtn('totalRevenue', 'Total Rev', true)}
              {headerBtn('revenuePerWarmer', 'R / Warmer', true)}
              {headerBtn('expected', 'Expected', true)}
              {headerBtn('variance', 'Variance', true)}
            </tr>
          </thead>
          <tbody>
            {sortedDays.map(d => {
              const varClr = d.variance == null
                ? 'text-gray-500'
                : d.variance < -0.01 ? 'text-red-400' : d.variance > 0.01 ? 'text-green-400' : 'text-gray-400'
              return (
                <tr key={d.date} className="border-b border-gray-800/50">
                  <td className="py-2 px-2 text-gray-300">{fmtShort(d.date)}</td>
                  <td className="py-2 px-2 text-right">
                    {d.warmers == null
                      ? '—'
                      : Number.isInteger(d.warmers) ? d.warmers : d.warmers.toFixed(1)}
                  </td>
                  <td className="py-2 px-2 text-right text-gray-400">{d.papOnly > 0 ? formatR(d.papOnly) : '—'}</td>
                  <td className="py-2 px-2 text-right text-gray-400">{d.fullPap > 0 ? formatR(d.fullPap) : '—'}</td>
                  <td className="py-2 px-2 text-right text-gray-400">{d.halfPap > 0 ? formatR(d.halfPap) : '—'}</td>
                  <td className="py-2 px-2 text-right text-gray-400">{d.platePap > 0 ? formatR(d.platePap) : '—'}</td>
                  <td className="py-2 px-2 text-right font-medium">{formatR(d.totalRevenue)}</td>
                  <td className="py-2 px-2 text-right">{d.revenuePerWarmer != null ? formatR(d.revenuePerWarmer) : '—'}</td>
                  <td className="py-2 px-2 text-right text-gray-400">{d.expected != null ? formatR(d.expected) : '—'}</td>
                  <td className={`py-2 px-2 text-right ${varClr}`}>
                    {d.variance == null
                      ? '—'
                      : d.variance === 0 ? formatR(0)
                      : `${d.variance > 0 ? '+' : '−'}${formatR(Math.abs(d.variance))}`}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
        {loadingWarmers && <p className="text-xs text-gray-500 text-center py-2">Loading warmer records…</p>}
      </Card>
    </>
  )
}
